import { useState } from 'react';
import { fmt, fmtDate, mean, sgn, stdev, type Snapshot } from '@balansa/shared';
import { f2 } from '../lib/geometry';
import { useApp, useWheel } from '../store';
import { Cn } from './Cn';

export function HistoryTab() {
  const { wheel, idx, en } = useWheel();
  const A = useApp.getState;
  const [note, setNote] = useState('');
  const snaps = wheel.snapshots.slice().sort((a, b) => b.ts - a.ts);
  const cmp = wheel.settings.compare ? wheel.snapshots.find((s) => s.id === wheel.settings.compare) ?? null : null;
  const snapStats = (s: Snapshot) => {
    const ids = (s.tops && s.tops.length ? s.tops : idx.tops).filter((id) => s.vals[id] != null);
    const v = ids.map((id) => s.vals[id]);
    return { avg: mean(v), sd: stdev(v), min: v.length ? Math.min(...v) : 0 };
  };

  return (
    <>
      <form
        className="snap-form"
        autoComplete="off"
        onSubmit={async (e) => {
          e.preventDefault();
          await A().addSnapshot(note.trim());
          setNote('');
        }}
      >
        <input className="inp" placeholder="Piezīme (neobligāti), piem. “pēc atvaļinājuma”" maxLength={80} value={note} onChange={(e) => setNote(e.target.value)} aria-label="Piezīme" />
        <button className="btn primary sm" type="submit">
          Saglabāt pašreizējo stāvokli
        </button>
      </form>
      {!snaps.length ? (
        <p className="empty">Vēl nav saglabātu stāvokļu. Saglabā pašreizējo, lai pēc laika redzētu, kā aplis mainās.</p>
      ) : (
        <>
          <Trend snaps={snaps} snapStats={snapStats} cur={idx.tops.map((t) => idx.val.get(t)!)} enL={en.pct} />
          {cmp ? (
            <div id="compare-table" onMouseLeave={() => A().setHover(null, null)}>
              <h3 className="h3">Salīdzinājums ar {fmtDate(cmp.ts)}</h3>
              <table className="ctable">
                <thead>
                  <tr>
                    <th>Joma</th>
                    <th>Tad</th>
                    <th>Tagad</th>
                    <th>Izmaiņa</th>
                  </tr>
                </thead>
                <tbody>
                  {idx.tops.map((t) => {
                    const now = idx.val.get(t)!;
                    const then = cmp.vals[t];
                    const d = then == null ? null : now - then;
                    const cls = d == null ? '' : d > 0.05 ? 'up' : d < -0.05 ? 'down' : '';
                    const txt = d == null ? '' : d > 0.05 ? '▲ ' + sgn(d) : d < -0.05 ? '▼ ' + sgn(d) : '=';
                    return (
                      <tr key={t} onClick={() => A().pick(t)} onMouseEnter={() => A().setHover(t, null)}>
                        <td>
                          <Cn idx={idx} id={t} />
                        </td>
                        <td>{then == null ? '—' : fmt(then)}</td>
                        <td>{fmt(now)}</td>
                        <td className={cls}>{txt}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}
          <h3 className="h3">Saglabātie stāvokļi</h3>
          <ul className="slist">
            {snaps.map((s) => {
              const st = snapStats(s);
              const on = !!(cmp && cmp.id === s.id);
              return (
                <li key={s.id} className={on ? 'on' : ''}>
                  <div className="s-main">
                    <div className="s-t">
                      {fmtDate(s.ts, true)}
                      {s.note ? (
                        <>
                          {' — '}
                          <span>{s.note}</span>
                        </>
                      ) : null}
                    </div>
                    <div className="s-s">
                      vidēji {fmt(st.avg)} · σ {fmt(st.sd)} · zemākā {fmt(st.min)}
                      {s.energy ? ' · enerģija ' + Math.round(s.energy.now) + (s.energy.cap ? '/' + Math.round(s.energy.cap) + ' v.' : '') : ''}
                    </div>
                  </div>
                  <div className="s-acts">
                    <button type="button" className={'btn sm' + (on ? ' primary' : '')} onClick={() => A().setSettings({ compare: on ? null : s.id })}>
                      {on ? 'Salīdzina ✓' : 'Salīdzināt'}
                    </button>
                    <button
                      type="button"
                      className="btn sm ghost"
                      title="Dzēst"
                      aria-label="Dzēst"
                      onClick={async () => {
                        if (await A().ask({ title: 'Dzēst šo saglabāto stāvokli?', ok: 'Dzēst', danger: true })) await A().deleteSnapshot(s.id);
                      }}
                    >
                      ×
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </>
      )}
      <div className="danger-zone">
        <button
          type="button"
          className="btn sm ghost danger"
          onClick={async () => {
            const ok = await A().ask({
              title: 'Atiestatīt uz sākuma struktūru?',
              message: 'Jomas, vērtības, saites, kārtis un vēsture tiks aizstātas ar noklusējuma apli (Veselība, Draugi, Ģimene, Darbs, Hobiji, Miegs, Uzturs). Ieteicams pirms tam “Eksportēt”.',
              ok: 'Atiestatīt',
              danger: true
            });
            if (ok) await A().resetWheel();
          }}
        >
          Atiestatīt uz sākuma struktūru…
        </button>
      </div>
    </>
  );
}

function Trend(p: { snaps: Snapshot[]; snapStats: (s: Snapshot) => { avg: number; min: number }; cur: number[]; enL: number }) {
  const pts = p.snaps
    .slice()
    .reverse()
    .map((s) => ({ ts: s.ts, en: s.energy ? Math.min(100, (s.energy.now / (s.energy.cap || 100)) * 100) : null, now: false, ...p.snapStats(s) }));
  pts.push({ ts: Date.now(), avg: mean(p.cur), min: p.cur.length ? Math.min(...p.cur) : 0, en: p.enL, now: true });
  const W = 380;
  const H = 104;
  const pl = 24;
  const pr = 14;
  const pT = 10;
  const pB = 22;
  const X = (i: number) => pl + (pts.length === 1 ? (W - pl - pr) / 2 : (i * (W - pl - pr)) / (pts.length - 1));
  const Y = (v: number) => pT + (1 - v / 10) * (H - pT - pB);
  const line = (k: 'avg' | 'min') => pts.map((q, i) => (i ? 'L' : 'M') + f2(X(i)) + ' ' + f2(Y(q[k]))).join('');
  const ep = pts.map((q, i) => (q.en == null ? null : ([X(i), Y(q.en / 10)] as [number, number]))).filter((x): x is [number, number] => !!x);
  return (
    <div className="trend">
      <h3 className="h3">Tendence</h3>
      <p className="muted">Nepārtrauktā līnija — vidējais, sarkanā raustītā — zemākā joma, dzeltenā punktētā — baterijas piepildījums (% no ietilpības ÷10).</p>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Vidējā līmeņa tendence">
        {[0, 5, 10].map((v) => (
          <g key={v}>
            <line x1={pl} x2={W - pr} y1={Y(v)} y2={Y(v)} style={{ stroke: 'var(--line)' }} />
            <text className="tl" x={pl - 6} y={Y(v) + 3} textAnchor="end">
              {v}
            </text>
          </g>
        ))}
        <path d={line('min')} fill="none" style={{ stroke: 'var(--danger)' }} strokeWidth="1.5" strokeDasharray="4 4" />
        <path d={line('avg')} fill="none" style={{ stroke: 'var(--accent)' }} strokeWidth="2.5" strokeLinejoin="round" />
        {ep.length >= 2 ? (
          <path d={ep.map((q, i) => (i ? 'L' : 'M') + f2(q[0]) + ' ' + f2(q[1])).join('')} fill="none" style={{ stroke: '#c98a1b' }} strokeWidth="2" strokeDasharray="1 4" strokeLinecap="round" />
        ) : null}
        {pts.map((q, i) => (
          <circle key={i} cx={f2(X(i))} cy={f2(Y(q.avg))} r={q.now ? 4.5 : 3.5} style={{ fill: q.now ? 'var(--surface)' : 'var(--accent)', stroke: 'var(--accent)' }} strokeWidth="2" />
        ))}
        <text className="tl" x={f2(X(0))} y={H - 6} textAnchor={pts.length === 1 ? 'middle' : 'start'}>
          {fmtDate(pts[0].ts)}
        </text>
        {pts.length > 1 ? (
          <text className="tl" x={f2(X(pts.length - 1))} y={H - 6} textAnchor="end">
            tagad
          </text>
        ) : null}
      </svg>
    </div>
  );
}
